import { List, Record } from "immutable";
import _ from "lodash";

import { OUTLINE_DEFAULT_COLUMN_WIDTH, OUTLINE_MIN_COLUMN_WIDTH } from "../../../constants";
import {
  MINDMAP_VIEW,
  ORIENTATION_ROW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  TIMELINE_VIEW
} from "../../../constants/identifiers";
import { columnDefaultWidths } from "../../../constants/outlineColumnIds";
import Dimensions, { scaleBackPixelsToFontSize } from "../../../constants/styles/dimensions";
import relationshipView from "../../../constants/styles/relationshipView";
import timelineView from "../../../constants/styles/timelineView";
import timelineGroupOptions from "../../../constants/timelineGroupOptions";
import { createFromJS, immutableRecords } from "../../../types";
import { moveTableColumnInList, updateTableColumnList } from "../../../utils/tableColumns";
import { calendarSelectors } from "../../definition/calendar";
// import dateFunctions, { isDateValid } from "../../definition/calendar/dateFunctions";
import dateFunctions from "../../definition/calendar/dateFunctions";
import { unitScaleForZoomLimit } from "../../definition/calendar/dateFunctions/unitScaleFunctions";
import itemSelectors from "../../items/selectors";
import { mindmapSelectors } from "../../mindmaps";
// import _, { isNil } from "lodash";
import { appStateSelectors } from "..";
import defaultScrollPositionToTimestamp from "./defaultScrollPositionToTimestamp";
import { getFirstViewFromLayout, nextViewInLayout } from "./utils";
// import { timelineRangeSelectors } from "../../timelineRange";

/** @typedef {import('immutable').Map} Map */

/**
 * Get the active view id if it has not been provided in the calling place
 * @param {string|undefined} viewId
 * @param {Map} globalState
 */
const getReplacingViewId = (viewId, globalState) =>
  _.isNil(viewId) ? appStateSelectors.getActiveViewId(globalState) : viewId;

/**
 * Replace with the new views
 * Keep the parameters of the view whose type is matching
 * any of the new view
 * If replacing new timeline view and there is no existed,
 * auto scroll to the date of now
 * @param {OpenViewsRecord.byId} viewsById
 * @param {Object} newViews
 * @param {TimelineRecord} prevGlobalState
 */
const replaceViewsInConditions = (viewsById, newViews, prevGlobalState) => {
  const prevOpenViewsById = appStateSelectors.getOpenViewsById(prevGlobalState);
  let scrollToNow = false;
  const newViewsById = createFromJS.OpenViewsRecord(newViews).byId.map(view => {
    const foundPrevView = prevOpenViewsById.find(prevView => prevView.viewType === view.viewType);
    if (!foundPrevView && view.viewType === TIMELINE_VIEW) {
      scrollToNow = true;
    }
    return foundPrevView ? foundPrevView.set("viewId", view.viewId) : view;
  });
  return scrollToNow
    ? defaultScrollPositionToTimestamp(
      newViewsById,
      dateFunctions.timestampForNow(calendarSelectors.getCalendar(prevGlobalState)),
      prevGlobalState
    )
    : newViewsById;
};

// const getAdjustedScrollPosition = (v, scrollPosition, zoom, globalState) => {
//   let newX = scrollPosition.x;
//   const widthOffset = v.size.width / 2;
//   const dateRangeFrom = timelineRangeSelectors.getRangeFrom(globalState);
//   const dateRangeTo = timelineRangeSelectors.getRangeTo(globalState);
//   const leftScroll = isDateValid(dateRangeFrom) ? dateRangeFrom.timestamp / zoom : null;
//   const rightScroll = isDateValid(dateRangeTo) ? dateRangeTo.timestamp / zoom : null;
//   if (!isNil(leftScroll) && newX < leftScroll - widthOffset) {
//     newX = leftScroll - widthOffset;
//   } else if (!isNil(rightScroll) && newX > rightScroll + widthOffset) {
//     newX = rightScroll + widthOffset;
//   }
//   return scrollPosition.set("x", newX);
// };

const recordScrollAndZoom = (state, viewIds, scrollPosition, zoom, _globalState) => {
  let mergeObj = {};
  if (scrollPosition) {
    mergeObj.scrollPosition = immutableRecords.PointRecord(scrollPosition);
  }
  if (zoom) {
    mergeObj.unitScale = zoom;
  }
  const result = state.map((v, vId) => {
    if (viewIds.indexOf(vId) < 0) {
      return v;
    }
    const viewType = v.viewType;
    if (viewType === TIMELINE_VIEW) {
      // Should adjust the scroll position according to the date range limits
      // const adjustedScrollPosition = getAdjustedScrollPosition(v, mergeObj.scrollPosition, zoom, globalState);
      // mergeObj.scrollPosition = adjustedScrollPosition;
      return v.update("timelineOptions", opt => opt.merge(mergeObj));
    }
    // TODO: Save scroll position of other view types (but not zoom)
    return v;
  });
  return result;
};

const moveTimelineScrollPositions = (state, seconds) => {
  return state.map(view => {
    const unitScale = view.timelineOptions.unitScale;
    const scrollPosition = view.timelineOptions.scrollPosition.x + seconds / unitScale;
    return view.setIn(["timelineOptions", "scrollPosition", "x"], scrollPosition);
  });
};

const changeZoomForAppView = (appView, newUnitScale) => {
  const width = appView.size.width;
  const centerPixel = appView.timelineOptions.scrollPosition.x + width / 2;
  const unitScale = appView.timelineOptions.unitScale;
  const centerTs = centerPixel * unitScale;
  const newCenterPixel = centerTs / newUnitScale;
  const newLeftPixel = newCenterPixel - width / 2;
  return appView.update("timelineOptions", opt =>
    opt.merge({
      unitScale: newUnitScale,
      scrollPosition: opt.scrollPosition.set("x", newLeftPixel)
    })
  );
};

const changeZoomSlider = (state, action, viewId, prevGlobalState) => {
  if (prevGlobalState.appState.scrollingIsLocked) {
    return state.map(s => {
      return changeZoomForAppView(s, action.value);
    });
  } else {
    return state.update(viewId, appView => changeZoomForAppView(appView, action.value));
  }
};

const changeZoomLimits = (state, limit1, limit2, globalState) => {
  const zoomFrom = Math.min(limit1, limit2);
  const zoomTo = Math.max(limit1, limit2);
  const calendar = calendarSelectors.getCalendar(globalState);
  const minUnitScale = unitScaleForZoomLimit(calendar, zoomTo, false);
  const maxUnitScale = unitScaleForZoomLimit(calendar, zoomFrom, true);

  return state.map(v => {
    // Work out our new zoom level
    const newScale = Math.min(maxUnitScale, Math.max(minUnitScale, v.timelineOptions.unitScale));
    // If it hasn't changed, do nothing
    if (newScale === v.timelineOptions.unitScale) {
      return v;
    }
    return changeZoomForAppView(v, newScale);
  });
};

const updateColumnList = (list, id, showColumn) => {
  const result = showColumn ? (list.contains(id) ? list : list.push(id)) : list.filter(cId => cId !== id);
  return result;
};

const updateColumnListForMultipleIds = (list, ids, showColumn) => {
  if (showColumn) {
    return list.withMutations(l => {
      ids.forEach(id => {
        if (!l.contains(id)) {
          l.push(id);
        }
      });
    });
  } else {
    return list.filter(id => !ids.contains(id));
  }
};

const updateColumnTypeIdList = (list, id, showColumn) => {
  if (showColumn) {
    return list.withMutations(l => {
      if (!l.contains(id)) {
        l.push(id);
      }
    });
  } else {
    return list.filter(typeId => typeId != id);
  }
};

const toggleRelationshipColumn = (state, action, prevGlobalState) => {
  // First, work out if they clicked on an individual item or a group
  const itemForId = prevGlobalState.data.items.byId.get(action.itemId);

  if (itemForId) {
    // Clicked on individual item
    // If we are syncing, update them all...
    // Otherwise, update the current list only...
    const typeId = prevGlobalState.definitions.types.byId.get(itemForId.type).id;
    if (prevGlobalState.appState.scrollingIsLocked) {
      return state.map(s =>
        s
          .updateIn(["relationshipOptions", "columnIds"], columnIds =>
            updateColumnList(columnIds, action.itemId, action.showColumn)
          )
          .updateIn(["relationshipOptions", "typeIds"], typeIds =>
            action.showColumn ? updateColumnTypeIdList(typeIds, typeId, action.showColumn) : typeIds
          )
      );
    } else {
      return state
        .updateIn([action.viewId, "relationshipOptions", "columnIds"], columnIds =>
          updateColumnList(columnIds, action.itemId, action.showColumn)
        )
        .updateIn([action.viewId, "relationshipOptions", "typeIds"], typeIds =>
          action.showColumn ? updateColumnTypeIdList(typeIds, typeId, action.showColumn) : typeIds
        );
    }
  } else {
    // Otherwise, click was not an individual item...
    // So we need to toggle the entire group!
    const itemIds = itemSelectors.getItemsOfType(prevGlobalState, action.itemId).map(o => o.id);
    if (prevGlobalState.appState.scrollingIsLocked) {
      return state.map(s =>
        s
          .updateIn(["relationshipOptions", "columnIds"], columnIds =>
            updateColumnListForMultipleIds(columnIds, itemIds, action.showColumn)
          )
          .updateIn(["relationshipOptions", "typeIds"], typeIds =>
            updateColumnTypeIdList(typeIds, action.itemId, action.showColumn)
          )
      );
    } else {
      return state
        .updateIn([action.viewId, "relationshipOptions", "columnIds"], columnIds =>
          updateColumnListForMultipleIds(columnIds, itemIds, action.showColumn)
        )
        .updateIn([action.viewId, "relationshipOptions", "typeIds"], typeIds =>
          updateColumnTypeIdList(typeIds, action.itemId, action.showColumn)
        );
    }
  }
};

const toggleSubwayThread = (state, action, prevGlobalState) => {
  // First, work out if they clicked on an individual item or a group
  const itemForId = prevGlobalState.data.items.byId.get(action.itemId);

  if (itemForId) {
    // Clicked on individual icon
    // If we are syncing, update them all...
    // Otherwise, update the current list only...
    return state.updateIn([action.viewId, "subwayOptions", "threadIds"], threadIds =>
      updateColumnList(threadIds, action.itemId, action.showThread)
    );
  } else {
    // Otherwise, click was not an individual item...
    // So we need to toggle the entire group!
    const itemIds = itemSelectors.getItemsOfType(prevGlobalState, action.itemId).map(o => o.id);
    return state.updateIn([action.viewId, "subwayOptions", "threadIds"], threadIds =>
      updateColumnListForMultipleIds(threadIds, itemIds, action.showThread)
    );
  }
};

const toggleTimelineGroup = (state, action, prevGlobalState) => {
  // First, work out if they clicked on an individual item or a group
  const typeForId = prevGlobalState.definitions.types.byId.get(action.itemOrTypeId);
  if (typeForId) {
    // Click was on a group, not an item... so we need to toggle all items
    // So we need to toggle the entire group!
    const itemIds = itemSelectors
      .getItemsOfType(prevGlobalState, typeForId.id)
      .map(o => o.id)
      .push(timelineGroupOptions.getUnallocatedGroupIdForType(typeForId.id));

    return state.updateIn([action.viewId, "timelineOptions", "groupIds"], groupIds =>
      updateColumnListForMultipleIds(groupIds, itemIds, action.showGroup)
    );
  } else {
    return state.updateIn([action.viewId, "timelineOptions", "groupIds"], groupIds =>
      updateColumnList(groupIds, action.itemOrTypeId, action.showGroup)
    );
  }
};

const clearTimelineGroups = (state, action, _prevGlobalState) => {
  return state.setIn([action.viewId, "timelineOptions", "groupIds"], List());
};

const toggleOutlineColumn = (state, action, prevGlobalState) => {
  // TODO: Make a better choice here based on the column type.
  const defaultWidth = OUTLINE_DEFAULT_COLUMN_WIDTH;
  // If we are syncing, update them all...
  // Otherwise, update the current list only...
  if (prevGlobalState.appState.scrollingIsLocked) {
    return state.map(s =>
      s.updateIn(["outlineOptions", "columns"], columns =>
        updateTableColumnList(columns, action.columnId, action.showColumn, defaultWidth, prevGlobalState)
      )
    );
  } else {
    return state.updateIn([action.viewId, "outlineOptions", "columns"], columns =>
      updateTableColumnList(columns, action.columnId, action.showColumn, defaultWidth, prevGlobalState)
    );
  }
};

const resizeOutlineColumn = (state, action, prevGlobalState) => {
  const width = Math.max(OUTLINE_MIN_COLUMN_WIDTH, action.width);
  // If we are syncing, update them all...
  // Otherwise, update the current list only...
  if (prevGlobalState.appState.scrollingIsLocked) {
    return state.map(s =>
      s.updateIn(["outlineOptions", "columns"], columns =>
        updateTableColumnList(columns, action.columnId, true, width, prevGlobalState)
      )
    );
  } else {
    return state.updateIn([action.viewId, "outlineOptions", "columns"], columns =>
      updateTableColumnList(columns, action.columnId, true, width, prevGlobalState)
    );
  }
};

const moveOutlineColumn = (state, action, prevGlobalState) => {
  // If we are syncing, update them all...
  // Otherwise, update the current list only...
  if (prevGlobalState.appState.scrollingIsLocked) {
    return state.map(s =>
      s.updateIn(["outlineOptions", "columns"], columns =>
        moveTableColumnInList(columns, action.columnId, action.index)
      )
    );
  } else {
    return state.updateIn([action.viewId, "outlineOptions", "columns"], columns =>
      moveTableColumnInList(columns, action.columnId, action.index)
    );
  }
};

const replaceTableColumns = (state, action) => {
  const { defaultColumns } = action;
  if (defaultColumns) {
    return state.map(s =>
      s
        .setIn(["spreadsheetOptions", "columns"], defaultColumns[SPREADSHEET_VIEW])
        .setIn(["outlineOptions", "columns"], defaultColumns[OUTLINE_VIEW])
    );
  }
  return state;
};

const replaceRelationshipViewColumnTypes = (state, action) => {
  const { typesById } = action;
  if (typesById) {
    const columnTypeIds = typesById
      .filter(t => t.showAsColumn && t.columnIsVisible && t.enabled)
      .map(t => t.id)
      .toList();
    return state.map(s => s.setIn(["relationshipOptions", "typeIds"], columnTypeIds));
  }
  return state;
};

const removeRelationshipColumnTypeIds = (state, ids) => {
  return state.map(s =>
    s.updateIn(["relationshipOptions", "typeIds"], typeIds => typeIds.filter(t => ids.indexOf(t) < 0))
  );
};

const removeRelationshipColumns = (state, ids) => {
  return state.map(s =>
    s.updateIn(["relationshipOptions", "columnIds"], columnIds => columnIds.filter(t => ids.indexOf(t) < 0))
  );
};

const removeSubwayThreads = (state, ids) => {
  return state.map(s =>
    s.updateIn(["subwayOptions", "threadIds"], threadIds => threadIds.filter(t => ids.indexOf(t) < 0))
  );
};

const removeTimelineGroups = (state, ids) => {
  return state.map(s =>
    s.updateIn(["timelineOptions", "groupIds"], groupIds => groupIds.filter(t => ids.indexOf(t) < 0))
  );
};

const toggleSpreadsheetColumn = (state, action, prevGlobalState) => {
  // TODO: Make a better choice here based on the column type.
  const defaultWidth = OUTLINE_DEFAULT_COLUMN_WIDTH;
  const width = columnDefaultWidths[action.columnId] || defaultWidth;
  // If we are syncing, update them all...
  // Otherwise, update the current list only...
  if (prevGlobalState.appState.scrollingIsLocked) {
    return state.map(s =>
      s.updateIn(["spreadsheetOptions", "columns"], columns =>
        updateTableColumnList(columns, action.columnId, action.showColumn, width, prevGlobalState)
      )
    );
  } else {
    return state.updateIn([action.viewId, "spreadsheetOptions", "columns"], columns =>
      updateTableColumnList(columns, action.columnId, action.showColumn, width, prevGlobalState)
    );
  }
};

const scaledBackColumnWidth = (width, prevGlobalState) => {
  const fontSize = appStateSelectors.getFontSize(prevGlobalState);
  return scaleBackPixelsToFontSize(width, fontSize);
};

const resizeSpreadsheetColumn = (state, action, prevGlobalState) => {
  const width = Math.max(OUTLINE_MIN_COLUMN_WIDTH, scaledBackColumnWidth(action.width, prevGlobalState));

  // If we are syncing, update them all...
  // Otherwise, update the current list only...
  if (prevGlobalState.appState.scrollingIsLocked) {
    return state.map(s =>
      s.updateIn(["spreadsheetOptions", "columns"], columns =>
        updateTableColumnList(columns, action.columnId, true, width, prevGlobalState)
      )
    );
  } else {
    return state.updateIn([action.viewId, "spreadsheetOptions", "columns"], columns =>
      updateTableColumnList(columns, action.columnId, true, width, prevGlobalState)
    );
  }
};

const sortSpreadsheetColumn = (state, viewId, action) => {
  let result = state;
  const currentSortBy = state.getIn([viewId, "spreadsheetOptions", "sortBy"]);
  if (currentSortBy === action.sortBy) {
    result = state.updateIn([viewId, "spreadsheetOptions", "sortInDesc"], sia => !sia);
  } else {
    result = state.mergeIn([viewId, "spreadsheetOptions"], { sortBy: action.sortBy, sortInDesc: false });
  }
  return result;
};

const moveSpreadsheetColumn = (state, action, prevGlobalState) => {
  // If we are syncing, update them all...
  // Otherwise, update the current list only...
  if (prevGlobalState.appState.scrollingIsLocked) {
    return state.map(s =>
      s.updateIn(["spreadsheetOptions", "columns"], columns =>
        moveTableColumnInList(columns, action.columnId, action.index)
      )
    );
  } else {
    return state.updateIn([action.viewId, "spreadsheetOptions", "columns"], columns =>
      moveTableColumnInList(columns, action.columnId, action.index)
    );
  }
};

const addView = (byIdState, newViewId, copiedView) =>
  byIdState.set(
    newViewId,
    copiedView.merge({
      viewId: newViewId,
      size: createFromJS.SizeRecord({
        width: 0,
        height: 0
      })
    })
  );

const getFirstView = globalState => {
  const rootLayout = appStateSelectors.getRootLayout(globalState);
  return getFirstViewFromLayout(rootLayout);
};

/**
 * This function can set more than two views.
 * Used to set three views that has been removed from the app.
 * @param {number} number Number of the views
 * @param {Immutable.Map} byIdState
 * @param {string} activeViewId
 * @param {string[]} newViewIds
 * @param {string} activeView
 * @param {TimelineRecord} globalState
 */
const setViewsOfNumber = (number, byIdState, activeViewId, newViewIds, activeView, globalState) => {
  const viewsCount = byIdState.count();
  let newState = byIdState;
  if (viewsCount < number) {
    for (let i = 0; i < number - viewsCount; i++) {
      newState = addView(newState, newViewIds[i], activeView);
    }
  } else if (viewsCount > number) {
    const rootLayout = appStateSelectors.getRootLayout(globalState);
    let nextViewId = activeViewId;
    let keepIds = [nextViewId];
    for (let i = 0; i < number - 1; i++) {
      nextViewId = nextViewInLayout(rootLayout, nextViewId) || getFirstViewFromLayout(rootLayout);
      keepIds.push(nextViewId);
    }
    newState = newState.filter((_v, id) => keepIds.includes(id));
  }
  return newState;
};

const setTwoViewsLayout = (byIdState, activeViewId, newViewId, activeView, globalState) => {
  return setViewsOfNumber(2, byIdState, activeViewId, [newViewId], activeView, globalState);
};

const getViewOptionNameByViewType = viewType => {
  switch (viewType) {
    case TIMELINE_VIEW:
      return "timelineOptions";
    default:
      return "";
  }
};

const getViewTypeOptionsOfSiblings = (viewId, viewsById, viewType, state) => {
  let result = null;
  const rootLayout = appStateSelectors.getRootLayout(state);

  const getViewTypeOptionsOfLayout = layout => {
    const children = layout.children;
    if (children.find(c => c === viewId) !== undefined) {
      const childrenWithoutTargetView = children.filter(c => c !== viewId);
      // Has other views, all view ids(no layout record), all view types are the same as the target type
      if (
        childrenWithoutTargetView.size > 0 &&
        childrenWithoutTargetView.every(c => typeof c === "string" && viewsById.get(c).viewType === viewType)
      ) {
        result = viewsById.get(childrenWithoutTargetView.get(0)).get(getViewOptionNameByViewType(viewType));
        return true;
      }
    } else {
      return children.find(c => Record.isRecord(c) && getViewTypeOptionsOfLayout(c)) !== undefined;
    }
  };

  if (viewType === TIMELINE_VIEW) {
    getViewTypeOptionsOfLayout(rootLayout);
  }

  return result;
};

/**
 * Find the target view and its siblings in the horizontal layout
 * It includes the parent as its siblings by default
 * Eg. [x, [y, z]], target is y, when all orientations are horizontal
 * will return [x, y, z]
 * Basically, the result will include all the horizontal stacked views with the target.
 * Unless the flag includeParentSiblings is off
 * @param {string} targetViewId
 * @param {string|ViewLayoutRecord} layout
 * @param {bool} includeParentSiblings whether involve the parent as siblings
 */
const getHorizontalSiblingViewIds = (targetViewId, layout, includeParentSiblings = true) => {
  let result = null;

  // Width first
  const findSiblingsIds = (layout, siblings) => {
    if (typeof layout === "string") {
      result = targetViewId === layout ? List([targetViewId].concat(siblings)) : null;
      return true;
    } else {
      const children = layout.children;
      if (children.find(c => c === targetViewId)) {
        result = layout.orientation === ORIENTATION_ROW ? children.concat(siblings) : null;
        return true;
      } else {
        return (
          children
            .filter(c => typeof c !== "string")
            .findIndex(c =>
              findSiblingsIds(c, layout.orientation === ORIENTATION_ROW ? children.filter(sc => sc !== c) : List())
            ) >= 0
        );
      }
    }
  };

  const findSiblingsIdsWithoutParent = layout => {
    if (typeof layout === "string") {
      result = targetViewId === layout ? List([targetViewId]) : null;
      return true;
    } else {
      const children = layout.children;
      if (children.find(c => c === targetViewId)) {
        result = layout.orientation === ORIENTATION_ROW ? children : null;
        return true;
      } else {
        return children.filter(c => typeof c !== "string").findIndex(c => findSiblingsIdsWithoutParent(c)) >= 0;
      }
    }
  };

  if (includeParentSiblings) {
    findSiblingsIds(layout, List());
  } else {
    findSiblingsIdsWithoutParent(layout);
  }

  return result;
};

/**
 * @param {string|ViewLayoutRecord} view
 */
const getFirstViewIdFromLayout = view =>
  typeof view === "string" ? view : view.children.find(c => typeof c === "string");

/**
 * @param {string|ViewLayoutRecord} view
 */
const isViewCollapsed = (view, viewsById) => {
  if (typeof view === "string") {
    return viewsById.get(view).size.height <= Dimensions.COLLAPSE_WORKVIEW_HEIGHT;
  } else {
    return view.children.every(c => isViewCollapsed(c, viewsById));
  }
};

const isSyncScrollLayout = (layout, viewsById, viewType) =>
  layout &&
  typeof layout !== "string" &&
  layout.orientation === ORIENTATION_ROW &&
  layout.children.every(c => typeof c === "string" && viewsById.get(c).viewType === viewType);

/**
 * Get the tallest height of a layout/view.
 * Return the view height directly.
 * For layout, return the max height between all its children
 * For sync scroll layout, return the total height
 * @param {string|ViewLayoutRecord} layout
 * @param {Map} viewsById
 */
const getMaxLayoutHeight = (layout, viewsById) => {
  if (layout) {
    if (typeof layout === "string") {
      return viewsById.getIn([layout, "size", "height"]);
    } else {
      let result = 0;
      const { children } = layout;
      // Special cases for the view layout with sync scroll views
      if (isSyncScrollLayout(layout, viewsById, TIMELINE_VIEW)) {
        result =
          layout.children.reduce((sum, c) => (sum += getMaxLayoutHeight(c, viewsById)), 0) +
          timelineView.HEADER_HEIGHT +
          timelineView.CONTEXT_BAR_HEIGHT +
          layout.children.count() * 1; // The split border height
      } else if (isSyncScrollLayout(layout, viewsById, RELATIONSHIP_VIEW)) {
        result =
          layout.children.reduce((sum, c) => (sum += getMaxLayoutHeight(c, viewsById)), 0) +
          relationshipView.HEADER_HEIGHT +
          layout.children.count() * 1; // The split border height
      } else {
        result = Math.max(...children.map(c => getMaxLayoutHeight(c, viewsById)).toArray());
      }
      return result;
    }
  }
  return 0;
};
const setLayoutTwoViewStackThirds = (byIdState, activeViewId, activeView, newViewId, globalState) => {
  const firstViewId = getFirstView(globalState);
  return setTwoViewsLayout(byIdState, activeViewId, newViewId, activeView, globalState).map((v, vId) =>
    v.setIn(["size", "height"], (vId === firstViewId ? 1 : 2) * Dimensions.MIN_WORKVIEW_HEIGHT)
  );
};

const switchViewType = (byIdState, targetViewId, viewType, prevGlobalState, mindmapId) => {
  // Get the scroll position of siblings
  const initialViewTypeOptions = getViewTypeOptionsOfSiblings(targetViewId, byIdState, viewType, prevGlobalState);
  const result = byIdState.update(targetViewId, v =>
    v.merge({
      viewType: viewType,
      mindmapOptions: createFromJS.MindmapViewOptionsRecord({
        mindmapId: mindmapId
      })
    })
  );
  return initialViewTypeOptions
    ? result.mergeIn([targetViewId, getViewOptionNameByViewType(viewType)], {
      scrollPosition: initialViewTypeOptions.scrollPosition,
      unitScale: initialViewTypeOptions.unitScale
    })
    : result;
};

const deleteMindmap = (byIdState, mindmapId, prevGlobalState) => {
  const replacingMindmap = mindmapSelectors
    .getMindmapList(prevGlobalState)
    .filter(m => m.id !== mindmapId)
    .get(0);
  const replacingId = replacingMindmap ? replacingMindmap.id : null;
  let result = byIdState;
  byIdState.forEach(v => {
    if (v.viewType === MINDMAP_VIEW && v.mindmapOptions.mindmapId === mindmapId) {
      result = switchViewType(
        result,
        v.viewId,
        replacingId ? MINDMAP_VIEW : TIMELINE_VIEW,
        prevGlobalState,
        replacingId
      );
    }
  });
  return result;
};

export {
  addView,
  changeZoomLimits,
  changeZoomSlider,
  clearTimelineGroups,
  deleteMindmap,
  getFirstView,
  getFirstViewIdFromLayout,
  getHorizontalSiblingViewIds,
  getMaxLayoutHeight,
  getReplacingViewId,
  getViewOptionNameByViewType,
  getViewTypeOptionsOfSiblings,
  isViewCollapsed,
  moveOutlineColumn,
  moveSpreadsheetColumn,
  moveTimelineScrollPositions,
  recordScrollAndZoom,
  removeRelationshipColumns,
  removeRelationshipColumnTypeIds,
  removeSubwayThreads,
  removeTimelineGroups,
  replaceRelationshipViewColumnTypes,
  replaceTableColumns,
  replaceViewsInConditions,
  resizeOutlineColumn,
  resizeSpreadsheetColumn,
  setLayoutTwoViewStackThirds,
  setTwoViewsLayout,
  setViewsOfNumber,
  sortSpreadsheetColumn,
  switchViewType,
  toggleOutlineColumn,
  toggleRelationshipColumn,
  toggleSpreadsheetColumn,
  toggleSubwayThread,
  toggleTimelineGroup
};
